import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import sequencesService from '@services/sequences';
import facturasService from '@services/facturas';
import clientesService from '@services/clientes';
import { MdAddCircleOutline, MdVisibility, MdPrint, MdMenu, MdClose, MdTrendingUp, MdAttachMoney, MdReceipt, MdTrendingDown } from 'react-icons/md';
import Menu from '@components/Menu';
import PDFPrint from "@components/PdfPrint";
import ReactTable from '@components/ReactTable';
import DocumentPrintModal from '@components/DocumentPrintModal';
import useDocumentPreview from '@hooks/useDocumentPreview';
import UserContext from '@components/context/userContext';
import { TimestampToDate } from '@utils/date';
import { threeDecimal } from '@utils/format';
import AutoCompleteV2 from '@components/AutoCompleteV2';
import DateRangePicker from '@components/DateRangePicker';

import "./list.css";
import TestEnvironmentAlert from '@components/TestEnvironmentAlert';

const getUrl = (invoice) => {
  switch (invoice.type) {
    case "recibos":
      return `/sales/${invoice.type}/${invoice.date_time}/${invoice.serie}/${invoice.numero}` 
    case "notas_credito_tpv":
    case "taloes":
      return `/sales/${invoice.type}/${invoice.posto}/${invoice.tpv}/${invoice.serie}/${invoice.numero}`  
    default:
      return `/sales/${invoice.type}/${invoice.serie}/${invoice.numero}`
  }
}

// Função para calcular timestamps do mês atual
const getCurrentMonthTimestamps = () => {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-11
  const currentYear = now.getFullYear();
  
  // Primeiro dia do mês atual
  const firstDayOfCurrentMonth = new Date(Date.UTC(currentYear, currentMonth, 1, 0, 0, 0, 0));
  // Último dia do mês atual
  const lastDayOfCurrentMonth = new Date(Date.UTC(currentYear, currentMonth + 1, 0, 23, 59, 59, 999));
  
  const firstUnixTimestamp = Math.floor(firstDayOfCurrentMonth.getTime() / 1000);
  const lastUnixTimestamp = Math.floor(lastDayOfCurrentMonth.getTime() / 1000);

  return { first: firstUnixTimestamp, last: lastUnixTimestamp };
};

// Função para calcular timestamps do mês anterior
const getPreviousMonthTimestamps = () => {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-11
  const currentYear = now.getFullYear();
  
  // Calcular mês anterior
  const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
  
  // Primeiro dia do mês anterior
  const firstDayOfLastMonth = new Date(Date.UTC(lastMonthYear, lastMonth, 1, 0, 0, 0, 0));
  // Último dia do mês anterior
  const lastDayOfLastMonth = new Date(Date.UTC(lastMonthYear, lastMonth + 1, 0, 23, 59, 59, 999));
  
  const firstUnixTimestamp = Math.floor(firstDayOfLastMonth.getTime() / 1000);
  const lastUnixTimestamp = Math.floor(lastDayOfLastMonth.getTime() / 1000);

  return { first: firstUnixTimestamp, last: lastUnixTimestamp };
};

// Função simplificada para calcular estatísticas usando dados do backend
const calculateStatistics = (currentMonthStats, lastMonthStats) => {
  // Valores padrão se não houver dados
  const current = currentMonthStats || {
    totalClientes: 0,
    totalResults: 0,
    valorLiquido: 0,
    totalAnulado: 0
  };
  
  const last = lastMonthStats || {
    totalClientes: 0,
    totalResults: 0,
    valorLiquido: 0,
    totalAnulado: 0
  };

  // Função para calcular variação percentual
  const calculateVariation = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  // Calcular documentos válidos (total - anulados)
  const validDocuments = current.totalResults - current.totalAnulado;
  const lastValidDocuments = last.totalResults - last.totalAnulado;

  // Calcular média por documento
  const averageValue = validDocuments > 0 ? current.valorLiquido / validDocuments : 0;
  const lastAverageValue = lastValidDocuments > 0 ? last.valorLiquido / lastValidDocuments : 0;

  return {
    // Card 1: Total de Documentos
    totalDocuments: current.totalResults,
    totalDocumentsVariation: calculateVariation(current.totalResults, last.totalResults),
    
    // Card 2: Valor Total
    totalValue: current.valorLiquido,
    totalValueVariation: calculateVariation(current.valorLiquido, last.valorLiquido),
    
    // Card 3: Documentos Anulados
    cancelledDocuments: current.totalAnulado,
    cancelledDocumentsVariation: calculateVariation(current.totalAnulado, last.totalAnulado),
    
    // Card 4: Clientes
    totalCustomers: current.totalClientes,
    totalCustomersVariation: calculateVariation(current.totalClientes, last.totalClientes),
    
    // Dados adicionais
    averageValue: averageValue,
    averageValueVariation: calculateVariation(averageValue, lastAverageValue),
    validDocuments: validDocuments,
    validDocumentsVariation: calculateVariation(validDocuments, lastValidDocuments),
    documentsByType: {}, // Mantido para compatibilidade, mas não calculado
    totalByType: {}, // Mantido para compatibilidade, mas não calculado
    monthlyStats: {} // Mantido para compatibilidade, mas não calculado
  };
};

const InvoiceList = () => {
  const [canCreate, setCanCreate] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [lastMonthInvoices, setLastMonthInvoices] = useState({
    totalClientes: 0,
    totalResults: 0,
    valorLiquido: 0,
    totalAnulado: 0
  });
  // Removido o estado year - agora usamos apenas filtro de data
  const [error, setError] = useState(null);
  const history = useHistory();
  const profile = useContext(UserContext);
  const [invoice, setInvoice] = useState({});
  const [selected, setSelected] = useState([]);
  const [multipleSelecition, setMultipleSelecition] = useState(false);
  // Estado de paginação
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0
  });
  const [tableStats, setTableStats] = useState({
    totalResults: 0,
    valorLiquido: 0,
    totalAnulado: 0
  });
  const [tableLoading, setTableLoading] = useState(false);
  const [statistics, setStatistics] = useState({
    totalValue: 0,
    totalValueVariation: 0,
    totalDocuments: 0,
    totalDocumentsVariation: 0,
    averageValue: 0,
    averageValueVariation: 0,
    documentsByType: {},
    validDocuments: 0,
    validDocumentsVariation: 0,
    cancelledDocuments: 0,
    cancelledDocumentsVariation: 0,
    totalByType: {},
    monthlyStats: {},
    totalCustomers: 0,
    totalCustomersVariation: 0
  });
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState({ value: "", label: "TODOS" });
  const [selectedCustomer, setSelectedCustomer] = useState({ value: "", label: "TODOS", customer_name: '' });
  const [customerOptions, setCustomerOptions] = useState([{ value: "", label: "TODOS" }]);
  const [documentSearch, setDocumentSearch] = useState("");
  const [debouncedDocumentSearch, setDebouncedDocumentSearch] = useState("");
  // Definir por defeito a data desde o início do ano até hoje
  const [date, setDate] = useState([
    new Date(new Date().getFullYear(), 0, 1).setHours(0, 0, 0), // Início do ano atual
    new Date().setHours(23, 59, 59) // Hoje
  ]);
  // Removido o estado showDatePicker pois não usamos mais modal

  // Debounce document search value changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedDocumentSearch(documentSearch);
    }, 500); // 500ms de delay

    return () => {
      clearTimeout(timeoutId);
    };
  }, [documentSearch]);

  // Use the generic document preview hook
  const {
    previewUrl,
    isSubmitting,
    printerRef,
    handleSingleDocumentPrint,
    handleBulkDocumentPrint,
    openPrintModal
  } = useDocumentPreview(profile);

  const doctypes = [
    { value: "", label: "TODOS" },
    { value: 'facturas', label: 'Faturas' },
    { value: 'taloes', label: 'Talões' },
    { value: 'vendas_dinheiro', label: 'Faturas Recibos' },
    { value: 'notas_credito', label: 'Notas Crédito' },
    { value: 'recibos', label: 'Recibos' },
    { value: 'guias_transporte', label: 'Guias Transporte' },
    { value: 'notas_credito_tpv', label: 'Notas Crédito TPV' },
  ];

  const columns = [
    {
      Header: 'Selecionar',
      accessor: 'selected',
    },
    {
      Header: 'Data',
      accessor: 'date',
    },
    {
      Header: 'Cliente',
      accessor: 'customer_name',
    },
    {
      Header: 'Documento',
      accessor: 'doc',
    },
    {
      Header: 'Tipo Doc',
      accessor: 'doctype',
    },
    {
      Header: 'Total',
      accessor: 'total',
    },
    {
      Header: 'Anulada',
      accessor: 'deleted',
    },
    {
      Header: '',
      accessor: 'view',
    },
  ];

  // Carregar estatísticas (sempre mês atual vs mês anterior)
  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        const [responseSeriesFT, responseSeriesFR] = await Promise.all([
          sequencesService.get(profile.idEmpresa, 'FT_epos'),
          sequencesService.get(profile.idEmpresa, 'FR_epos')
        ]);

        if (!responseSeriesFT.length || !responseSeriesFR.length) {
          setCanCreate(false);
        } else {
          setCanCreate(true);
        }

        // Usar timestamps do mês atual para estatísticas
        const currentMonthTimestamps = getCurrentMonthTimestamps();
        const previousMonthTimestamps = getPreviousMonthTimestamps();

        // Fazer duas chamadas à API em paralelo para estatísticas
        // Usar stats do backend em vez de calcular no frontend
        const [currentMonthResponse, lastMonthResponse] = await Promise.all([
          facturasService.get(profile.idEmpresa, {
            start_date: currentMonthTimestamps.first,
            end_date: currentMonthTimestamps.last,
            page: 1,
            pageSize: 1
          }),
          facturasService.get(profile.idEmpresa, {
            start_date: previousMonthTimestamps.first,
            end_date: previousMonthTimestamps.last,
            page: 1,
            pageSize: 1
          })
        ]);
        
        // Extrair stats da resposta da API
        const currentMonthStats = currentMonthResponse?.stats || {
          totalClientes: 0,
          totalResults: 0,
          valorLiquido: 0,
          totalAnulado: 0
        };
        const lastMonthStats = lastMonthResponse?.stats || {
          totalClientes: 0,
          totalResults: 0,
          valorLiquido: 0,
          totalAnulado: 0
        };

        // Calcular estatísticas usando os stats do backend
        const stats = calculateStatistics(currentMonthStats, lastMonthStats);
        setStatistics(stats);
        
        // Guardar stats do mês anterior para comparação quando cliente mudar
        setLastMonthInvoices(lastMonthStats);
      } catch (err) {
        console.error('Error fetching statistics:', err);
        setError('Erro ao carregar estatísticas');
      }
    };

    fetchStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.idEmpresa]);

  // Carregar opções de clientes baseadas nos dados do mês atual das estatísticas
  useEffect(() => {
    const loadCustomerOptions = async () => {
        try {
          const currentMonthTimestamps = getCurrentMonthTimestamps();
          const currentMonthResponse = await facturasService.get(profile.idEmpresa, {
            start_date: currentMonthTimestamps.first,
            end_date: currentMonthTimestamps.last,
            page: 1,
            pageSize: 1 // Pedir muitas entradas para opções de clientes
          });

          const currentMonthData = currentMonthResponse?.entries || [];
          if (currentMonthData && currentMonthData.length > 0) {
            const uniqueCustomers = [];
            const customerMap = new Map();
            
            currentMonthData.forEach(invoice => {
              if (invoice.customer_name && !customerMap.has(invoice.customer_name)) {
                customerMap.set(invoice.customer_name, true);
                uniqueCustomers.push({
                  value: invoice.customer_name,
                  label: invoice.customer_name,
                  customer_name: invoice.customer_name
                });
              }
            });
            
            // Ordenar por nome
            uniqueCustomers.sort((a, b) => a.customer_name.localeCompare(b.customer_name));
            const clientes = await clientesService.get(profile.idEmpresa);
            const enrichedClientes = clientes.map((cliente) => ({ value: cliente.codigo, label: cliente.nome_1 }));
            setCustomerOptions([{ value: "", label: "TODOS" }, ...enrichedClientes]);
          }
      } catch (err) {
        console.error('Error loading customer options:', err);
      }
    };

    loadCustomerOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.idEmpresa]);

  // Recalcular estatísticas quando o cliente selecionado mudar
  useEffect(() => {
    if (lastMonthInvoices && typeof lastMonthInvoices === 'object' && lastMonthInvoices.totalResults !== undefined) {
      // Obter stats do mês atual com filtro de cliente se aplicável
      const fetchCurrentMonthForStats = async () => {
        try {
          const currentMonthTimestamps = getCurrentMonthTimestamps();
          const apiFilters = {
            start_date: currentMonthTimestamps.first,
            end_date: currentMonthTimestamps.last,
            page: 1,
            pageSize: 1
          };

          // Adicionar filtro de cliente se selecionado
          if (selectedCustomer && selectedCustomer.value && selectedCustomer.value !== "") {
            apiFilters.cliente = selectedCustomer.value;
          }

          const currentMonthResponse = await facturasService.get(profile.idEmpresa, apiFilters);
          
          const currentMonthStats = currentMonthResponse?.stats || {
            totalClientes: 0,
            totalResults: 0,
            valorLiquido: 0,
            totalAnulado: 0
          };
          
          const stats = calculateStatistics(currentMonthStats, lastMonthInvoices);
          setStatistics(stats);
        } catch (err) {
          console.error('Error recalculating statistics:', err);
        }
      };

      fetchCurrentMonthForStats();
    }
  }, [selectedCustomer, lastMonthInvoices, profile.idEmpresa]);

  // Carregar dados da tabela baseados no filtro de data e paginação
  useEffect(() => {
    const fetchTableData = async () => {
      try {
        setTableLoading(true);
        // Usar o filtro de data para a tabela
        const startTimestamp = Math.floor(date[0] / 1000);
        const endTimestamp = Math.floor(date[1] / 1000);

        // Aplicar filtros de tipo de documento e cliente na API se necessário
        const apiFilters = {
          start_date: startTimestamp,
          end_date: endTimestamp,
          page: pagination.page,
          pageSize: pagination.pageSize
        };

        // Adicionar filtros opcionais
        if (selectedDocumentTypes && selectedDocumentTypes.value) {
          apiFilters.type = selectedDocumentTypes.value;
        }

        // Adicionar filtro de cliente se selecionado
        if (selectedCustomer && selectedCustomer.value && selectedCustomer.value !== "") {
          apiFilters.cliente = selectedCustomer.value;
        }

        // Adicionar filtro de pesquisa de documento se preenchido
        if (debouncedDocumentSearch && debouncedDocumentSearch.trim() !== "") {
          apiFilters.search = debouncedDocumentSearch.trim();
        }

        const tableResponse = await facturasService.get(profile.idEmpresa, apiFilters);

        console.log('Debug - Table data range:', {
          start: new Date(startTimestamp * 1000),
          end: new Date(endTimestamp * 1000)
        });
        console.log('Debug - Table response:', tableResponse);

        // Processar nova estrutura de resposta
        const entries = tableResponse.entries || [];
        const stats = tableResponse.stats || { totalResults: 0, valorLiquido: 0, totalAnulado: 0 };

        setTableStats(stats);
        setPagination(prev => ({
          ...prev,
          totalPages: tableResponse.totalPages || 0,
          totalItems: tableResponse.totalItems || 0
        }));

        if (!entries.length) {
          setInvoices([]);
          setTableLoading(false);
          return;
        }

        const enrichedTableInvoices = entries.map(invoice => {
          // Para guias_transporte, mostrar apenas a data (sem hora)
          const isGuiaTransporte = invoice.type === 'guias_transporte';
          const dateValue = isGuiaTransporte 
            ? TimestampToDate(invoice.date_time)
            : TimestampToDate(invoice.date_time); // Data e hora completa
          
          return {
            ...invoice,
            date: dateValue,
            doctype: (doctypes.find(dt => dt.value === invoice.type) || {}).label,
            total: threeDecimal(invoice.total),
            deleted: invoice.valid ? '' : 'TRUE',
            view: (
              <table className="actionButtons" style={{ width: '150px', fontSize: 'small' }}>
                <tr style={{ lineHeight: '10px' }}>
                  <td
                    style={{ cursor: "pointer", padding: '3px' }}
                    onClick={() => history.push(getUrl(invoice))}
                  >
                    <MdVisibility/>
                  </td>
                  <td
                    style={{ cursor: "pointer", padding: '3px' }}
                    onClick={() => {
                      setInvoice(invoice);
                      openPrintModal();
                    }}
                  >
                    <MdPrint/>
                  </td>
                </tr>
              </table>
            ),
          };
        });

        setInvoices(enrichedTableInvoices);
        setTableLoading(false);
      } catch (err) {
        console.error('Error fetching table data:', err);
        setError('Erro ao carregar dados da tabela');
        setTableLoading(false);
      }
    };

    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.idEmpresa, date, pagination.page, pagination.pageSize, selectedDocumentTypes, selectedCustomer, debouncedDocumentSearch]);

  const handlePrint = async ({ selectedPageSize, selectedPageType }, invoiceData = null) => {
    const documentToPrint = invoiceData || invoice;
    await handleSingleDocumentPrint(
      documentToPrint,
      selectedPageSize,
      selectedPageType,
      {
        endpoint: 'facturas'
      }
    );
  };

  const handleBulkPrint = async ({ selectedPageSize, selectedPageType }) => {
    await handleBulkDocumentPrint(selected, selectedPageSize, selectedPageType, {
      endpoint: 'facturas'
    });
  };

  const handlePrintClick = ({ selectedPageSize, selectedPageType }) => {
    if (multipleSelecition && selected.length) {
      handleBulkPrint({ selectedPageSize, selectedPageType });
    } else {
      handlePrint({ selectedPageSize, selectedPageType });
    }
  };

  // Função para retornar as faturas (todos os filtros são aplicados na API)
  const getFilteredInvoices = () => {
    return invoices;
  };

  // Função para mudar de página (react-table usa índice 0-based)
  const handlePageChange = (pageIndex) => {
    setPagination(prev => ({ ...prev, page: pageIndex + 1 }));
  };

  // Função para mudar o tamanho da página
  const handlePageSizeChange = (pageSize, pageIndex) => {
    setPagination(prev => ({ ...prev, pageSize, page: 1 }));
  };

  // Função para atualizar filtro de tipo de documento
  const handleDocumentTypeChange = (event, newValue) => {
    // Trabalhar com objeto único em vez de array
    const safeValue = newValue || { value: "", label: "TODOS" };
    console.log('Document type change:', { newValue, safeValue });
    setSelectedDocumentTypes(safeValue);
  };

  // Função para atualizar filtro de cliente
  const handleCustomerChange = (event, newValue) => {
    const safeValue = newValue || { value: "", label: "TODOS", customer_name: '' };
    console.log('Customer change:', { newValue, safeValue });
    setSelectedCustomer(safeValue);
    // Resetar paginação para a primeira página quando o cliente mudar
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Função para atualizar filtro de datas
  const localDateChange = (newDate) => {
    setDate([
      newDate[0].setHours(0, 0, 0),
      newDate[1].setHours(23, 59, 59),
    ]);
  };

  // Função para atualizar filtro de pesquisa de documento
  const handleDocumentSearchChange = (event) => {
    setDocumentSearch(event.target.value);
    // Resetar paginação para a primeira página quando a pesquisa mudar
    setPagination(prev => ({ ...prev, page: 1 }));
  };


  return (
    <div>
      <Menu />
      <div className="fullContainer" style={{ maxWidth: '100%' }}>
        <div className="col-xs-12">
          <ol
            style={{
              textAlign: 'left',
              marginTop: '75px',
            }}
            className="breadcrumb"
          >
            <li>
              <a href="http://eposgestofinal.com/index.php">Portal</a>
            </li>
            <li className="active">Faturação</li>
          </ol>
        </div>
      </div>
      <TestEnvironmentAlert />
      
      {/* Seção de Estatísticas Ultra Compacta com Detalhes */}
      <div className="stats-section">
        <div className="stats-section-header">
          <h3 className="stats-title">
            Estatísticas - {new Date().toLocaleString('pt-PT', { month: 'long', year: 'numeric' })}
            {selectedCustomer && selectedCustomer.value && (
              <span className="stats-subtitle"> - {selectedCustomer.label}</span>
            )}
          </h3>
        </div>
        <div className="stats-cards-container">
          {/* Card 1: Total de Documentos */}
          <div className="stat-card total">
            <div className="stat-card-header">
              <div className="stat-card-icon">
                <MdReceipt />
              </div>
              <div className="stat-card-content">
                <div className="stat-value">
                  {statistics.totalDocuments.toLocaleString()}
                </div>
                <p className="stat-label">Documentos</p>
              </div>
            </div>
            <div className={`stat-change ${statistics.totalDocumentsVariation >= 0 ? 'positive' : 'negative'}`}>
              {statistics.totalDocumentsVariation >= 0 ? <MdTrendingUp /> : <MdTrendingDown />}
              {statistics.totalDocumentsVariation >= 0 ? '+' : ''}{statistics.totalDocumentsVariation.toFixed(1)}% (mês anterior)
            </div>
            <div className="stat-comparison">
              <div className="comparison-item">
                <span className="comparison-label">Válidos:</span>
                <span className="comparison-value">{statistics.validDocuments}</span>
              </div>
              <div className="comparison-item">
                <span className="comparison-label">Anulados:</span>
                <span className="comparison-value">{statistics.cancelledDocuments}</span>
              </div>
            </div>
          </div>

          {/* Card 2: Valor Total */}
          <div className="stat-card value">
            <div className="stat-card-header">
              <div className="stat-card-icon">
                <MdAttachMoney />
              </div>
              <div className="stat-card-content">
                <div className="stat-value">
                  {statistics.totalValue.toLocaleString('pt-PT', { minimumFractionDigits: 0 })} €
                </div>
                <p className="stat-label">Valor Total</p>
              </div>
            </div>
            <div className={`stat-change ${statistics.totalValueVariation >= 0 ? 'positive' : 'negative'}`}>
              {statistics.totalValueVariation >= 0 ? <MdTrendingUp /> : <MdTrendingDown />}
              {statistics.totalValueVariation >= 0 ? '+' : ''}{statistics.totalValueVariation.toFixed(1)}% (mês anterior)
            </div>
            <div className="stat-comparison">
              <div className="comparison-item">
                <span className="comparison-label">Média por doc:</span>
                <span className="comparison-value">{threeDecimal(statistics.averageValue)} €</span>
              </div>
            </div>
          </div>

          {/* Card 3: Documentos Anulados */}
          <div className="stat-card pending">
            <div className="stat-card-header">
              <div className="stat-card-icon">
                <MdTrendingUp />
              </div>
              <div className="stat-card-content">
                <div className="stat-value">
                  {statistics.cancelledDocuments}
                </div>
                <p className="stat-label">Anulados</p>
              </div>
            </div>
            <div className={`stat-change ${statistics.cancelledDocumentsVariation >= 0 ? 'positive' : 'negative'}`}>
              {statistics.cancelledDocumentsVariation >= 0 ? <MdTrendingUp /> : <MdTrendingDown />}
              {statistics.cancelledDocumentsVariation >= 0 ? '+' : ''}{statistics.cancelledDocumentsVariation.toFixed(1)}% (mês anterior)
            </div>
            <div className="stat-comparison">
              <div className="comparison-item">
                <span className="comparison-label">% do Total:</span>
                <span className="comparison-value">
                  {statistics.totalDocuments > 0 
                    ? ((statistics.cancelledDocuments / statistics.totalDocuments) * 100).toFixed(1) 
                    : 0}%
                </span>
              </div>
              <div className="comparison-item">
                <span className="comparison-label">Válidos:</span>
                <span className="comparison-value">{statistics.validDocuments}</span>
              </div>
            </div>
          </div>

          {/* Card 4: Clientes */}
          <div className="stat-card providers">
            <div className="stat-card-header">
              <div className="stat-card-icon">
                <MdTrendingUp />
              </div>
              <div className="stat-card-content">
                <div className="stat-value">
                  {statistics.totalCustomers}
                </div>
                <p className="stat-label">Clientes</p>
              </div>
            </div>
            <div className={`stat-change ${statistics.totalCustomersVariation >= 0 ? 'positive' : 'negative'}`}>
              {statistics.totalCustomersVariation >= 0 ? <MdTrendingUp /> : <MdTrendingDown />}
              {statistics.totalCustomersVariation >= 0 ? '+' : ''}{statistics.totalCustomersVariation.toFixed(1)}% (mês anterior)
            </div>
            <div className="stat-comparison">
              <div className="comparison-item">
                <span className="comparison-label">Únicos:</span>
                <span className="comparison-value">{statistics.totalCustomers}</span>
              </div>
              <div className="comparison-item">
                <span className="comparison-label">Com Vendas:</span>
                <span className="comparison-value">
                  {customerOptions.filter(customer => 
                    invoices.some(invoice => invoice.customer_name === customer.customer_name)
                  ).length}
                </span>
              </div>
              {selectedCustomer && (
                <div className="comparison-item">
                  <span className="comparison-label">Selecionado:</span>
                  <span className="comparison-value">{selectedCustomer.customer_name}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Seção de Filtros Moderna */}
      <div style={{ maxWidth: '100%' }}>
          <div className="filters-section">
        <div className="filters-header">
          <h3 className="filters-title">
            Filtros de Documentos
          </h3>
          <button 
                className="create-document-btn"
                onClick={() => canCreate ? history.push('/sales/new') : alert('Não foram detectadas séries no ePOS.')}
                disabled={!canCreate}
                onMouseEnter={(e) => {
                  if (canCreate) {
                    e.target.style.backgroundColor = '#0056b3';
                  }
                }}
                onMouseLeave={(e) => {
                  if (canCreate) {
                    e.target.style.backgroundColor = '#007bff';
                  }
                }}
              >
                <MdAddCircleOutline style={{ fontSize: '18px' }} />
                <span>Criar Documento</span>
              </button>
        </div>
        
        <div className="filters-container">
          {/* Filtro por intervalo de datas */}
          <div className="filter-group">
            <label className="filter-label" style={{ 
              display: 'block', 
              marginBottom: '5px', 
              fontWeight: 'bold', 
              fontSize: '12px', 
              color: '#495057' 
            }}>
              Período
            </label>
            <div className="date-filter-container">
              <DateRangePicker 
                maxDate={new Date()} 
                value={date} 
                onChange={localDateChange}
                withPortal={false}
              />
            </div>
          </div>
          
          <div className="filter-group">
            <label className="filter-label" style={{ 
              display: 'block', 
              marginBottom: '5px', 
              fontWeight: 'bold', 
              fontSize: '12px', 
              color: '#495057' 
            }}>
              Tipo de Documento
            </label>
            <AutoCompleteV2
              options={doctypes}
              value={selectedDocumentTypes}
              onChange={handleDocumentTypeChange}
              label="Selecionar tipo de documento"
              getOptionLabel={(option) => option?.label || ''}
              className="document-type-filter"
              placeholder="Todos os tipos"
            />
          </div>
          
          <div className="filter-group">
            <label className="filter-label" style={{ 
              display: 'block', 
              marginBottom: '5px', 
              fontWeight: 'bold', 
              fontSize: '12px', 
              color: '#495057' 
            }}>
              Cliente
            </label>
            <AutoCompleteV2
              options={customerOptions}
              value={selectedCustomer}
              onChange={handleCustomerChange}
              label="Selecionar cliente"
              getOptionLabel={(option) => option?.label || option?.customer_name || ''}
              className="customer-filter"
              noResultsText="Nenhum cliente encontrado"
            />
          </div>
          
          <div className="filter-group">
            <label className="filter-label" style={{ 
              display: 'block', 
              marginBottom: '5px', 
              fontWeight: 'bold', 
              fontSize: '12px', 
              color: '#495057' 
            }}>
              Pesquisar Documento
            </label>
            <input
              type="text"
              value={documentSearch}
              onChange={handleDocumentSearchChange}
              placeholder="Nº Documento"
              className="document-search-input"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '12px',
                backgroundColor: '#ffffff',
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#007bff';
                e.target.style.boxShadow = '0 0 0 3px rgba(0, 123, 255, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>
      </div>
      </div>



      <div className="fullContainer" style={{ maxWidth: '100%' }}>
        <div className="col-sm-12" style={{ textAlign: 'center' }}>
          <div className="panel panel-default">
            <div
              className="panel-heading"
              style={{
                fontWeight: 'bold',
                fontSize: '15pt',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '15px 20px'
              }}
            >
              <div style={{ textAlign: 'left' }}>
                        Faturação
              </div>
              <div>
            {multipleSelecition ? (
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <div className='label-selected-documents'>
                  {selected.length ? (
                    <>
                      <span style={{ fontWeight: 'bolder' }}>{selected.length}</span> documento{selected.length > 1 ? 's': ''} selecionado{selected.length > 1 ? 's': ''}
                    </>
                  ) : (
                    <>
                      nenhum documento selecionado
                    </>
                  )}
                </div>
                <div style={{ textAlign: 'right', paddingRight: '20px' }}>
                  {/* <button className="btn btn-block btn-bulk-print" disabled style={{ margin: '10px', width: 'auto', padding: 0 }} onClick={() => {}}>
                    <MdDownload style={{ width: '18px', height: '18px' }} />
                    <span style={{ fontWeight: 'normal' }}>Download (Brevemente)</span>
                  </button> */}
                  <button className="btn btn-block btn-bulk-print" disabled={!selected.length} style={{ margin: '10px', width: 'auto', padding: 0 }} onClick={() => {
                    console.log('Botão imprimir clicado, documentos selecionados:', selected.length);
                    openPrintModal();
                  }}>
                    <MdPrint style={{ width: '18px', height: '18px' }} /> 
                    <span style={{ fontWeight: 'normal' }}>Imprimir</span>
                  </button>
                  <button className="btn btn-block btn-bulk-actions" style={{ margin: '10px', width: 'auto', padding: 0 }} onClick={() => { setSelected([]); setMultipleSelecition(false) }}>
                    <MdClose style={{ width: '18px', height: '18px', marginRight: 0 }} />
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'right', paddingRight: '20px' }}>
                <button className="btn btn-block btn-bulk-actions" style={{ margin: '10px', width: 'auto', padding: 0 }} onClick={() => { setMultipleSelecition(true) }}>
                  <MdMenu style={{ width: '18px', height: '18px', marginRight: 0 }} />
                </button>
              </div>
            )}
              </div>
            </div>


            {error ? (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            ) : (
              <>
              <ReactTable
                columns={columns.filter((column) => {
                  if (column.accessor === 'selected') {
                    return multipleSelecition;
                  }
                  if (column.accessor === 'view') {
                    return !multipleSelecition;
                  }
                  return true
                })}
                data={getFilteredInvoices().map((invoice) => {
                  return selected.find((sel) => sel.doc === invoice.doc) ? ({
                    ...invoice,
                    selected: (
                      <input
                        type="checkbox"
                        checked
                        onChange={(e) => setSelected(selected.filter((sel) => sel.doc !== invoice.doc))}
                      />
                    )
                  }) : ({
                    ...invoice,
                    selected: (
                      <input
                        type="checkbox"
                        checked={false}
                        onChange={(e) => setSelected([...selected, invoice])}
                      />
                    )
                  })
                })}
                sortable={true}
                loading={tableLoading}
                page={pagination.page - 1}
                pageSize={pagination.pageSize}
                pages={pagination.totalPages}
                manual
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
              </>
            )}
          </div>
        </div>
      </div>
      <PDFPrint ref={printerRef} pdfUrl={previewUrl} />
      <DocumentPrintModal
        onPrint={handlePrintClick}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default InvoiceList;